/**
 * components/BetaApplication/steps/StyleExperienceStep.tsx
 * Style & Experience Step - Step 3 of Beta Application
 *
 * Collects trading style and experience information:
 * - Trading experience level
 * - Trading style with conditional other specification
 * - Trading sessions with conditional other specification
 * - Current journaling method
 */

// 1. External dependencies
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';

// 2. Internal absolute imports
import { Form, Input, Select, SelectItem, TextArea, Card, CardHeader, CardBody } from '@enfomentis/ui';
import { useBetaApplicationData } from '../hooks/useBetaApplicationData';
import {
  validateStyleExperienceStep,
  validateRequiredSelection,
  validateRequiredMultiSelection,
  validateTradingStyleOther,
  validateTradingSessionsOther,
  validateTextField
} from '../utils/betaApplicationValidation';
import {
  ExperienceLevelEnum,
  TradingStyleEnum,
  TradingSessionsEnum,
  type ExperienceLevel,
  type TradingStyle,
  type TradingSessions
} from '../types/BetaApplicationTypes';

/**
 * Style & Experience Step Component
 *
 * Complete functional form for collecting trading style and experience
 * with conditional logic for "other" specifications and real-time validation.
 */
export const StyleExperienceStep: React.FC = () => {
  const { data: draft, updateData } = useBetaApplicationData();

  // Field touched states for validation timing
  const [experienceLevelTouched, setExperienceLevelTouched] = useState(false);
  const [tradingStyleTouched, setTradingStyleTouched] = useState(false);
  const [tradingStyleOtherTouched, setTradingStyleOtherTouched] = useState(false);
  const [tradingSessionsTouched, setTradingSessionsTouched] = useState(false);
  const [tradingSessionsOtherTouched, setTradingSessionsOtherTouched] = useState(false);
  const [currentJournalingTouched, setCurrentJournalingTouched] = useState(false);

  // Autofill detection and re-render management
  const [renderKey, setRenderKey] = useState(0);
  const autofillTimeoutRef = useRef<number | null>(null);
  const lastValuesRef = useRef({ tradingStyleOther: '', tradingSessionsOther: '', currentJournaling: '' });

  // Debounced autofill completion detection
  const triggerAutofillFix = useCallback(() => {
    // Clear any existing timeout
    if (autofillTimeoutRef.current) {
      window.clearTimeout(autofillTimeoutRef.current);
    }

    // Set a new timeout to trigger re-render after autofill is likely complete
    autofillTimeoutRef.current = window.setTimeout(() => {
      setRenderKey(prev => prev + 1);
    }, 150); // 150ms delay to allow autofill to complete multiple fields
  }, []);

  // Initialize lastValuesRef with current draft values
  useEffect(() => {
    if (draft) {
      lastValuesRef.current = {
        tradingStyleOther: draft.trading.tradingStyleOther || '',
        tradingSessionsOther: draft.trading.tradingSessionsOther || '',
        currentJournaling: draft.trading.currentJournaling
      };
    }
  }, [draft]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autofillTimeoutRef.current) {
        window.clearTimeout(autofillTimeoutRef.current);
      }
    };
  }, []);

  // Local validation functions for HeroUI components
  const validateExperienceLevelField = (value: string): string | null => {
    const result = validateRequiredSelection(value, "Experience level");
    return result.isValid ? null : (result.error || null);
  };

  const validateTradingStyleField = (value: string): string | null => {
    const result = validateRequiredSelection(value, "Trading style");
    return result.isValid ? null : (result.error || null);
  };

  const validateTradingStyleOtherField = useCallback((value: string): string | null => {
    if (!draft) return null;
    const result = validateTradingStyleOther(draft.trading.tradingStyle, value);
    return result.isValid ? null : (result.error || null);
  }, [draft]);

  const validateTradingSessionsField = (value: TradingSessions[]): string | null => {
    const result = validateRequiredMultiSelection(value, "Trading sessions");
    return result.isValid ? null : (result.error || null);
  };

  const validateTradingSessionsOtherField = useCallback((value: string): string | null => {
    if (!draft) return null;
    const sessions = Array.isArray(draft.trading.tradingSessions) ? draft.trading.tradingSessions : [];
    const result = validateTradingSessionsOther(sessions, value);
    return result.isValid ? null : (result.error || null);
  }, [draft]);

  const validateCurrentJournalingField = (value: string): string | null => {
    const result = validateTextField(value, "Current journaling method", 500);
    return result.isValid ? null : (result.error || null);
  };

  // Real-time validation - only show errors after field is touched
  const experienceLevelError = useMemo(() => {
    if (!experienceLevelTouched || !draft) return null;
    return validateExperienceLevelField(draft.trading.experienceLevel);
  }, [experienceLevelTouched, draft]);

  const tradingStyleError = useMemo(() => {
    if (!tradingStyleTouched || !draft) return null;
    return validateTradingStyleField(draft.trading.tradingStyle);
  }, [tradingStyleTouched, draft]);

  const tradingStyleOtherError = useMemo(() => {
    if (!tradingStyleOtherTouched || !draft) return null;
    return validateTradingStyleOtherField(draft.trading.tradingStyleOther || '');
  }, [tradingStyleOtherTouched, draft, validateTradingStyleOtherField]);

  const tradingSessionsError = useMemo(() => {
    if (!tradingSessionsTouched || !draft) return null;
    const sessions = Array.isArray(draft.trading.tradingSessions) ? draft.trading.tradingSessions : [];
    return validateTradingSessionsField(sessions);
  }, [tradingSessionsTouched, draft]);

  const tradingSessionsOtherError = useMemo(() => {
    if (!tradingSessionsOtherTouched || !draft) return null;
    return validateTradingSessionsOtherField(draft.trading.tradingSessionsOther || '');
  }, [tradingSessionsOtherTouched, draft, validateTradingSessionsOtherField]);

  const currentJournalingError = useMemo(() => {
    if (!currentJournalingTouched || !draft) return null;
    return validateCurrentJournalingField(draft.trading.currentJournaling);
  }, [currentJournalingTouched, draft]);

  // Update step completion status when validation changes
  useEffect(() => {
    if (!draft) return;

    const isStepValid = validateStyleExperienceStep(draft).isValid;
    const isCompleted = draft.completedSteps.includes('style-experience');

    if (isStepValid && !isCompleted) {
      updateData((current) => ({
        ...current,
        completedSteps: [...current.completedSteps, 'style-experience']
      }));
    } else if (!isStepValid && isCompleted) {
      updateData((current) => ({
        ...current,
        completedSteps: current.completedSteps.filter(step => step !== 'style-experience')
      }));
    }
  }, [draft, updateData]);

  // Form submission handler (for enter key behavior)
  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    // Form submission is handled by WizardLayout navigation
  };

  // Field change handlers
  const handleExperienceLevelChange = (value: ExperienceLevel | ''): void => {
    updateData((current) => ({
      ...current,
      trading: { ...current.trading, experienceLevel: value },
      lastModified: new Date().toISOString()
    }));
  };

  const handleTradingStyleChange = (value: TradingStyle | ''): void => {
    updateData((current) => {
      const updatedTrading = { ...current.trading, tradingStyle: value };

      // Clear trading style other when switching away from 'other'
      if (value !== 'other') {
        updatedTrading.tradingStyleOther = '';
        setTradingStyleOtherTouched(false);
      }

      return {
        ...current,
        trading: updatedTrading,
        lastModified: new Date().toISOString()
      };
    });
  };

  const handleTradingStyleOtherChange = (value: string): void => {
    const oldValue = lastValuesRef.current.tradingStyleOther;
    lastValuesRef.current.tradingStyleOther = value;

    // Detect if this might be autofill (rapid value change from empty to filled)
    if (oldValue === '' && value !== '' && value.length > 3) {
      triggerAutofillFix();
    }

    updateData((current) => ({
      ...current,
      trading: { ...current.trading, tradingStyleOther: value },
      lastModified: new Date().toISOString()
    }));
  };

  const handleTradingSessionsChange = (values: TradingSessions[]): void => {
    updateData((current) => {
      const updatedTrading = { ...current.trading, tradingSessions: values };

      // Clear trading sessions other when 'other' is not selected
      if (!values.includes('other')) {
        updatedTrading.tradingSessionsOther = '';
        setTradingSessionsOtherTouched(false);
      }

      return {
        ...current,
        trading: updatedTrading,
        lastModified: new Date().toISOString()
      };
    });
  };

  const handleTradingSessionsOtherChange = (value: string): void => {
    const oldValue = lastValuesRef.current.tradingSessionsOther;
    lastValuesRef.current.tradingSessionsOther = value;

    // Detect if this might be autofill (rapid value change from empty to filled)
    if (oldValue === '' && value !== '' && value.length > 3) {
      triggerAutofillFix();
    }

    updateData((current) => ({
      ...current,
      trading: { ...current.trading, tradingSessionsOther: value },
      lastModified: new Date().toISOString()
    }));
  };

  const handleCurrentJournalingChange = (value: string): void => {
    const oldValue = lastValuesRef.current.currentJournaling;
    lastValuesRef.current.currentJournaling = value;

    // Detect if this might be autofill (rapid value change from empty to filled)
    if (oldValue === '' && value !== '' && value.length > 5) {
      triggerAutofillFix();
    }

    updateData((current) => ({
      ...current,
      trading: { ...current.trading, currentJournaling: value },
      lastModified: new Date().toISOString()
    }));
  };

  if (!draft) {
    return <div>Loading...</div>;
  }

  const showTradingSessionsOther = Array.isArray(draft.trading.tradingSessions) && draft.trading.tradingSessions.includes('other');

  return (
    <Card
      radius='none'
      shadow='none'
      className="max-w-2xl mx-auto"
    >
      <CardHeader>
        <div className="text-2xl font-semibold mb-2">
          <h2>Trading Style & Experience</h2>
        </div>
      </CardHeader>

      <CardBody>
        <Form
          validationBehavior="aria"
          onSubmit={handleSubmit}
          className="space-y-6"
        >
          {/* Experience Level Selection */}
          <Select
            label="Experience Level"
            labelPlacement="outside"
            placeholder="Select your trading experience level"
            description="How long have you been trading?"
            selectedKeys={draft.trading.experienceLevel ? [draft.trading.experienceLevel] : []}
            onSelectionChange={(keys) => {
              const value = Array.from(keys)[0] as ExperienceLevel | undefined;
              handleExperienceLevelChange(value || '');
            }}
            onClose={() => setExperienceLevelTouched(true)}
            isRequired
            errorMessage={experienceLevelError}
            isInvalid={!!experienceLevelError}
            classNames={{
              trigger: "text-base", // 16px font size to prevent mobile zoom
            }}
        >
          <SelectItem key={ExperienceLevelEnum.LessThanSixMonths}>
            Less than 6 months
          </SelectItem>
          <SelectItem key={ExperienceLevelEnum.SixMonthsToTwoYears}>
            6 months - 2 years
          </SelectItem>
          <SelectItem key={ExperienceLevelEnum.TwoToFiveYears}>
            2-5 years
          </SelectItem>
          <SelectItem key={ExperienceLevelEnum.FivePlusYears}>
            5+ years
          </SelectItem>
        </Select>

          {/* Trading Style Selection */}
          <Select
            label="Trading Style"
            labelPlacement="outside"
            placeholder="Select your primary trading style"
            description="What type of trading do you primarily do?"
            selectedKeys={draft.trading.tradingStyle ? [draft.trading.tradingStyle] : []}
            onSelectionChange={(keys) => {
              const value = Array.from(keys)[0] as TradingStyle | undefined;
              handleTradingStyleChange(value || '');
            }}
            onClose={() => setTradingStyleTouched(true)}
            isRequired
            errorMessage={tradingStyleError}
            isInvalid={!!tradingStyleError}
            classNames={{
              trigger: "text-base",
            }}
        >
          <SelectItem key={TradingStyleEnum.Scalper}>
            Scalper (seconds to minutes)
          </SelectItem>
          <SelectItem key={TradingStyleEnum.DayTrader}>
            Day Trader (minutes to hours)
          </SelectItem>
          <SelectItem key={TradingStyleEnum.SwingTrader}>
            Swing Trader (days to weeks)
          </SelectItem>
          <SelectItem key={TradingStyleEnum.PositionalTrader}>
            Positional Trader / Investor (weeks, months or years)
          </SelectItem>
          <SelectItem key={TradingStyleEnum.Other}>
            Other (please specify)
          </SelectItem>
        </Select>

          {/* Conditional Trading Style Other Input */}
          {draft.trading.tradingStyle === 'other' && (
            <Input
              key={`tradingStyleOther-${renderKey}`}
              type="text"
              label="Please specify your trading style"
              labelPlacement="outside-top"
              placeholder="Describe your trading style"
              description="Briefly describe your unique trading approach"
              value={draft.trading.tradingStyleOther || ''}
              onValueChange={handleTradingStyleOtherChange}
              onBlur={() => setTradingStyleOtherTouched(true)}
              validate={validateTradingStyleOtherField}
              isRequired
              isClearable
              errorMessage={tradingStyleOtherError}
              isInvalid={!!tradingStyleOtherError}
              classNames={{
                input: "text-base",
              }}
            />
          )}

          {/* Trading Sessions Selection */}
          <Select
            label="Primary Trading Sessions"
            labelPlacement="outside"
            placeholder="Select your preferred trading sessions"
            description="When do you primarily trade? (Select multiple if applicable)"
            selectionMode="multiple"
            selectedKeys={Array.isArray(draft.trading.tradingSessions) ? draft.trading.tradingSessions : []}
            onSelectionChange={(keys) => {
              const values = Array.from(keys) as TradingSessions[];
              setTradingSessionsTouched(true);
              handleTradingSessionsChange(values);
            }}
            isRequired
            isClearable
            errorMessage={tradingSessionsError}
            isInvalid={!!tradingSessionsError}
            classNames={{
              trigger: "text-base",
            }}
        >
          <SelectItem key={TradingSessionsEnum.Asian}>
            Asian Session
          </SelectItem>
          <SelectItem key={TradingSessionsEnum.London}>
            London Session
          </SelectItem>
          <SelectItem key={TradingSessionsEnum.NewYorkAM}>
            New York AM Session
          </SelectItem>
          <SelectItem key={TradingSessionsEnum.NewYorkPM}>
            New York PM Session
          </SelectItem>
          <SelectItem key={TradingSessionsEnum.Other}>
            Other (please specify)
          </SelectItem>
        </Select>

          {/* Conditional Trading Sessions Other Input */}
          {showTradingSessionsOther && (
            <Input
              key={`tradingSessionsOther-${renderKey}`}
              type="text"
              label="Please specify your trading sessions"
              labelPlacement="outside-top"
              placeholder="Describe your trading schedule"
              description="When do you typically trade?"
              value={draft.trading.tradingSessionsOther || ''}
              onValueChange={handleTradingSessionsOtherChange}
              onBlur={() => setTradingSessionsOtherTouched(true)}
              validate={validateTradingSessionsOtherField}
              isRequired
              isClearable
              errorMessage={tradingSessionsOtherError}
              isInvalid={!!tradingSessionsOtherError}
              classNames={{
                input: "text-base",
              }}
            />
          )}

          {/* Current Journaling Method */}
          <TextArea
            key={`currentJournaling-${renderKey}`}
            label="Current Journaling Method"
            labelPlacement="outside-top"
            placeholder="Describe how you currently track your trades"
            description="How do you currently journal or track your trading performance? (Excel, apps, notebook, etc.)"
            value={draft.trading.currentJournaling}
            onValueChange={handleCurrentJournalingChange}
            onBlur={() => setCurrentJournalingTouched(true)}
            validate={validateCurrentJournalingField}
            isRequired
            minRows={3}
            maxRows={6}
            errorMessage={currentJournalingError}
            isInvalid={!!currentJournalingError}
            classNames={{
              input: "text-base  pt-2",
            }}
          />
        </Form>
      </CardBody>
    </Card>
  );
};